import { LpCard, LpDropdownMenu, LpEmpty, LpIcon, html, } from "~/design-system";
import { revenueInsightsChartMetrics } from "~/components/revenue-insights/daily-breakdown";
import "amcharts3/amcharts/amcharts";
import "amcharts3/amcharts/serial";
import "amcharts3/amcharts/pie";
import "amcharts3/amcharts/themes/light";
const REVENUE_TREND_CHART_ID = "revenue-insights-revenue-trend-chart";
const REVENUE_MIX_CHART_ID = "revenue-insights-revenue-mix-chart";
const VOLUME_CHART_ID = "revenue-insights-volume-chart";
let revenueTrendChart = null;
let revenueMixChart = null;
let volumeChart = null;
const selectableChartMetricKeys = new Set([
    "totalRevenue",
    "revenuePerUser",
    "clickRevenuePerUserConverted",
    "revenuePerClickout",
    "revenuePerLead",
]);
const chartMetrics = revenueInsightsChartMetrics
    .filter((metric) => selectableChartMetricKeys.has(metric.key))
    .map((metric) => ({
    key: metric.key,
    label: metric.label,
    format: "currency",
}));
const defaultChartMetric = chartMetrics.find(({ key }) => key === "totalRevenue") ?? chartMetrics[0];
function getChartElements() {
    return [
        document.getElementById(REVENUE_TREND_CHART_ID),
        document.getElementById(VOLUME_CHART_ID),
    ];
}
function areChartElementsReady() {
    return getChartElements().every((element) => element !== null && element.offsetWidth > 0 && element.offsetHeight > 0);
}
function toNumber(value) {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
}
function formatNumber(value, digits = 0) {
    return value.toLocaleString(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
}
function formatCurrency(value) {
    return value.toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
    });
}
function formatMetricValue(metric, value) {
    if (metric.format === "currency") {
        return formatCurrency(value);
    }
    if (metric.format === "percent") {
        return `${formatNumber(value, 2)}%`;
    }
    return formatNumber(value);
}
function getChartRows(listData, metricKey) {
    return listData
        .map((row) => ({
        ...row,
        dateData: String(row.dateData ?? ""),
        totalRevenue: toNumber(row.totalRevenue),
        leadRevenue: toNumber(row.leadRevenue),
        clickoutRevenue: toNumber(row.clickoutRevenue),
        leadsSold: toNumber(row.leadsSold),
        clickouts: toNumber(row.clickouts),
        [metricKey]: toNumber(row[metricKey]),
    }))
        .filter((row) => row.dateData !== "")
        .sort((first, second) => String(first.dateData).localeCompare(String(second.dateData)));
}
function getTotals(rows, totalsData) {
    const explicitTotals = totalsData[0];
    if (explicitTotals) {
        return explicitTotals;
    }
    return rows.reduce((totals, row) => {
        for (const key of ["totalRevenue", "clickoutRevenue", "leadRevenue", "leadsSold", "clickouts"]) {
            totals[key] = toNumber(totals[key]) + toNumber(row[key]);
        }
        return totals;
    }, {});
}
function getTrendSummaryNumbers(rows, metric) {
    const values = rows.map((row) => toNumber(row[metric.key]));
    const total = values.reduce((sum, value) => sum + value, 0);
    const average = values.length > 0 ? total / values.length : 0;
    const peak = values.length > 0 ? Math.max(...values) : 0;
    return [
        { label: "Total", value: formatMetricValue(metric, total) },
        { label: "Average", value: formatMetricValue(metric, average) },
        { label: "Peak Day", value: formatMetricValue(metric, peak) },
    ];
}
function getRevenueMixSummaryNumbers(rows, totalsData) {
    const totals = getTotals(rows, totalsData);
    const leadRevenue = toNumber(totals.leadRevenue);
    const clickoutRevenue = toNumber(totals.clickoutRevenue);
    const totalRevenue = toNumber(totals.totalRevenue);
    return [
        { label: "Total Revenue", value: formatCurrency(totalRevenue) },
        { label: "Lead Revenue", value: formatCurrency(leadRevenue) },
        { label: "Clickout Revenue", value: formatCurrency(clickoutRevenue) },
    ];
}
function getVolumeSummaryNumbers(rows, metric) {
    const leadsSold = rows.reduce((sum, row) => sum + toNumber(row.leadsSold), 0);
    const clickouts = rows.reduce((sum, row) => sum + toNumber(row.clickouts), 0);
    const metricTotal = rows.reduce((sum, row) => sum + toNumber(row[metric.key]), 0);
    return [
        { label: "Leads", value: formatNumber(leadsSold) },
        { label: "Clickouts", value: formatNumber(clickouts) },
        { label: metric.label, value: formatMetricValue(metric, metricTotal) },
    ];
}
function clearChart(chart) {
    chart?.clear?.();
    return null;
}
function removeAmChartsCreditLink(elementId) {
    const element = document.getElementById(elementId);
    const creditLink = Array.from(element?.querySelectorAll("a") ?? [])
        .find((link) => link.textContent?.includes("JS chart by amCharts"));
    creditLink?.remove();
}
function getCssVariableValue(name, fallback) {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}
function getChartTextColor() {
    const isDarkTheme = document.documentElement.dataset.theme === "dark";
    return isDarkTheme
        ? getCssVariableValue("--color-text", "#dcdcdc")
        : getCssVariableValue("--color-text-secondary", "#667085");
}
function getCommonSerialConfig(dataProvider) {
    const axisTextColor = getChartTextColor();
    return {
        type: "serial",
        theme: "light",
        dataProvider,
        categoryField: "dateData",
        addClassNames: true,
        autoMargins: false,
        marginTop: 12,
        marginRight: 24,
        marginBottom: 64,
        marginLeft: 72,
        fontFamily: "inherit",
        fontSize: 12,
        color: axisTextColor,
        creditsPosition: "bottom-right",
        hideCredits: true,
        categoryAxis: {
            gridPosition: "start",
            gridAlpha: 0,
            axisAlpha: 0.2,
            color: axisTextColor,
            labelRotation: 35,
        },
        chartCursor: {
            cursorAlpha: 0.12,
            categoryBalloonDateFormat: "MMM DD",
        },
        balloon: {
            adjustBorderColor: false,
            fillAlpha: 0.98,
            fillColor: "#1e3a8a",
            borderAlpha: 1,
            borderColor: "#60a5fa",
            color: "#ffffff",
            cornerRadius: 8,
            shadowAlpha: 0.2,
        },
        valueAxes: [{
                gridAlpha: 0.08,
                axisAlpha: 0,
                color: axisTextColor,
                minimum: 0,
            }],
    };
}
function getMetricBalloonText(metric) {
    if (metric.format === "currency") {
        return `[[category]]: $[[value]]`;
    }
    if (metric.format === "percent") {
        return `[[category]]: [[value]]%`;
    }
    return `[[category]]: [[value]]`;
}
function renderRevenueTrendChart(dataProvider, metric) {
    if (!window.AmCharts || !document.getElementById(REVENUE_TREND_CHART_ID)) {
        return null;
    }
    return window.AmCharts.makeChart(REVENUE_TREND_CHART_ID, {
        ...getCommonSerialConfig(dataProvider),
        graphs: [{
                id: metric.key,
                title: metric.label,
                type: "smoothedLine",
                valueField: metric.key,
                bullet: "round",
                bulletSize: 5,
                lineThickness: 3,
                lineColor: "#2f6fec",
                fillAlphas: 0.12,
                balloonText: getMetricBalloonText(metric),
            }],
    });
}
function renderRevenueMixChart(rows, totalsData) {
    if (!window.AmCharts || !document.getElementById(REVENUE_MIX_CHART_ID)) {
        return null;
    }
    const totals = getTotals(rows, totalsData);
    const leadRevenue = toNumber(totals.leadRevenue);
    const clickoutRevenue = toNumber(totals.clickoutRevenue);
    const emailRevenue = [1, 2, 3].reduce((sum, index) => sum + toNumber(totals[`emailRetargetingRevenue${index}`]), 0);
    const categorizedRevenue = leadRevenue + clickoutRevenue + emailRevenue;
    const otherRevenue = Math.max(0, toNumber(totals.totalRevenue) - categorizedRevenue);
    const dataProvider = [
        { label: "Lead Revenue", value: leadRevenue, color: "#2f6fec" },
        { label: "Clickout Revenue", value: clickoutRevenue, color: "#38bdf8" },
        { label: "Email Retargeting", value: emailRevenue, color: "#8b5cf6" },
        { label: "Other Revenue", value: otherRevenue, color: "#94a3b8" },
    ].filter(({ value }) => value > 0);
    return window.AmCharts.makeChart(REVENUE_MIX_CHART_ID, {
        type: "pie",
        theme: "light",
        dataProvider,
        titleField: "label",
        valueField: "value",
        colorField: "color",
        innerRadius: "64%",
        labelsEnabled: false,
        balloonText: "[[title]]: $[[value]]",
        pullOutRadius: 0,
        marginTop: 0,
        marginRight: 0,
        marginBottom: 0,
        marginLeft: 0,
        fontFamily: "inherit",
        fontSize: 12,
        hideCredits: true,
        balloon: {
            adjustBorderColor: false,
            fillAlpha: 0.98,
            fillColor: "#1e3a8a",
            borderAlpha: 1,
            borderColor: "#60a5fa",
            color: "#ffffff",
            cornerRadius: 8,
            shadowAlpha: 0.2,
        },
    });
}
function renderVolumeChart(dataProvider, metric) {
    if (!window.AmCharts || !document.getElementById(VOLUME_CHART_ID)) {
        return null;
    }
    const textColor = getChartTextColor();
    return window.AmCharts.makeChart(VOLUME_CHART_ID, {
        ...getCommonSerialConfig(dataProvider),
        legend: {
            useGraphSettings: true,
            markerSize: 8,
            valueWidth: 0,
            align: "center",
            color: textColor,
        },
        valueAxes: [
            {
                id: "volumeAxis",
                gridAlpha: 0.08,
                axisAlpha: 0,
                color: textColor,
                minimum: 0,
            },
            {
                id: "metricAxis",
                position: "right",
                gridAlpha: 0,
                axisAlpha: 0,
                color: textColor,
                minimum: 0,
            },
        ],
        graphs: [
            {
                title: "Leads",
                type: "column",
                valueField: "leadsSold",
                valueAxis: "volumeAxis",
                fillAlphas: 0.85,
                lineAlpha: 0,
                fillColors: "#2f6fec",
                balloonText: "[[category]]: [[value]] leads",
            },
            {
                title: "Clickouts",
                type: "column",
                valueField: "clickouts",
                valueAxis: "volumeAxis",
                fillAlphas: 0.75,
                lineAlpha: 0,
                fillColors: "#38bdf8",
                balloonText: "[[category]]: [[value]] clickouts",
            },
            {
                id: `selected-${metric.key}`,
                title: metric.label,
                type: "smoothedLine",
                valueField: metric.key,
                valueAxis: "metricAxis",
                bullet: "round",
                bulletSize: 4,
                lineThickness: 2,
                lineColor: "#8b5cf6",
                balloonText: getMetricBalloonText(metric),
            },
        ],
    });
}
export function createRevenueInsightsChartState(ko, listData, totalsData) {
    const selectedChartMetric = ko.observable(defaultChartMetric.key);
    const selectedMetric = ko.pureComputed(() => chartMetrics.find(({ key }) => key === selectedChartMetric()) ?? defaultChartMetric);
    const chartRows = ko.pureComputed(() => getChartRows(listData(), selectedMetric().key));
    return {
        hasChartData: ko.pureComputed(() => listData().length > 0),
        selectedChartMetric,
        selectedChartMetricLabel: ko.pureComputed(() => selectedMetric().label),
        trendSummaryNumbers: ko.pureComputed(() => getTrendSummaryNumbers(chartRows(), selectedMetric())),
        revenueMixSummaryNumbers: ko.pureComputed(() => getRevenueMixSummaryNumbers(chartRows(), totalsData())),
        volumeSummaryNumbers: ko.pureComputed(() => getVolumeSummaryNumbers(chartRows(), selectedMetric())),
        onChartMetricSelect(_data, event) {
            const metricKey = event.detail?.value;
            if (!metricKey || !chartMetrics.some(({ key }) => key === metricKey)) {
                return;
            }
            selectedChartMetric(metricKey);
            renderRevenueInsightsCharts(listData(), totalsData(), metricKey);
        },
    };
}
export function renderRevenueInsightsCharts(listData, totalsData, selectedMetricKey = defaultChartMetric.key) {
    const selectedMetric = chartMetrics.find(({ key }) => key === selectedMetricKey)
        ?? defaultChartMetric;
    const rows = getChartRows(listData, selectedMetric.key);
    revenueTrendChart = clearChart(revenueTrendChart);
    revenueMixChart = clearChart(revenueMixChart);
    volumeChart = clearChart(volumeChart);
    if (rows.length === 0) {
        return;
    }
    const renderCharts = (attempt = 0) => {
        if (!areChartElementsReady() && attempt < 8) {
            window.requestAnimationFrame(() => renderCharts(attempt + 1));
            return;
        }
        revenueTrendChart = renderRevenueTrendChart(rows, selectedMetric);
        volumeChart = renderVolumeChart(rows, selectedMetric);
        for (const chart of [revenueTrendChart, volumeChart]) {
            chart?.validateSize?.();
        }
        for (const elementId of [REVENUE_TREND_CHART_ID, VOLUME_CHART_ID]) {
            removeAmChartsCreditLink(elementId);
        }
    };
    window.requestAnimationFrame(() => renderCharts());
}
export function destroyRevenueInsightsCharts() {
    revenueTrendChart = clearChart(revenueTrendChart);
    revenueMixChart = clearChart(revenueMixChart);
    volumeChart = clearChart(volumeChart);
}
function renderRevenueTrendCard() {
    return LpCard({
        title: html `
            ${LpIcon({ name: "chart-line-up", size: 16 })}
            <span>Performance Trend</span>
        `,
        description: "Chart any metric available in the breakdown table.",
        className: "revenue-chart-card",
        actions: html `
            <div class="lp-select-field revenue-chart-metric-field">
                ${LpDropdownMenu.Root({
            className: "revenue-chart-metric-dropdown",
            attributes: {
                "data-bind": "event: { 'lp-dropdown-select': onChartMetricSelect }",
            },
            trigger: (context) => LpDropdownMenu.Trigger({
                ...context,
                label: html `
                            <span class="revenue-chart-metric-trigger__label">Metric : </span>
                            <span data-bind="text: selectedChartMetricLabel"></span>
                        `,
                prefixIcon: "chart-line-up",
                suffixIcon: "caret-down",
                variant: "secondary",
                className: "revenue-chart-metric-trigger",
                ariaLabel: "Select chart metric",
            }),
            content: (context) => LpDropdownMenu.Content({
                ...context,
                label: "Chart metric",
                align: "end",
                className: "revenue-chart-metric-menu",
                children: LpDropdownMenu.RadioGroup({
                    label: "Chart metric",
                    children: chartMetrics.map((metric) => LpDropdownMenu.RadioItem({
                        label: metric.label,
                        value: metric.key,
                        checked: metric.key === defaultChartMetric.key,
                        closeOnClick: true,
                        attributes: {
                            "data-bind": `attr: { 'aria-checked': selectedChartMetric() === '${metric.key}' }`,
                        },
                    })),
                }),
            }),
        })}
            </div>
        `,
        children: html `
            <div class="revenue-chart-numbers" data-bind="foreach: trendSummaryNumbers">
                <div class="revenue-chart-number">
                    <span class="revenue-chart-number__label" data-bind="text: label"></span>
                    <strong class="revenue-chart-number__value" data-bind="text: value"></strong>
                </div>
            </div>
            <div id="${REVENUE_TREND_CHART_ID}" class="revenue-amchart" aria-label="Revenue trend chart"></div>
        `,
    });
}
function renderRevenueMixCard() {
    return LpCard({
        title: html `
            ${LpIcon({ name: "chart-donut", size: 16 })}
            <span>Revenue Mix</span>
        `,
        description: "Lead revenue compared with clickout revenue.",
        className: "revenue-chart-card",
        children: html `
            <div class="revenue-chart-numbers" data-bind="foreach: revenueMixSummaryNumbers">
                <div class="revenue-chart-number">
                    <span class="revenue-chart-number__label" data-bind="text: label"></span>
                    <strong class="revenue-chart-number__value" data-bind="text: value"></strong>
                </div>
            </div>
            <div id="${REVENUE_MIX_CHART_ID}" class="revenue-amchart revenue-amchart--pie" aria-label="Revenue mix chart"></div>
        `,
    });
}
function renderVolumeCard() {
    return LpCard({
        title: html `
            ${LpIcon({ name: "chart-bar", size: 16 })}
            <span>Lead & Click Volume</span>
        `,
        description: "Leads, clickouts, and the selected metric across the selected date range.",
        className: "revenue-chart-card",
        children: html `
            <div class="revenue-chart-numbers" data-bind="foreach: volumeSummaryNumbers">
                <div class="revenue-chart-number">
                    <span class="revenue-chart-number__label" data-bind="text: label"></span>
                    <strong class="revenue-chart-number__value" data-bind="text: value"></strong>
                </div>
            </div>
            <div id="${VOLUME_CHART_ID}" class="revenue-amchart" aria-label="Lead and click volume chart"></div>
        `,
    });
}
export function renderRevenueInsightsSummaryCharts() {
    const chartCards = [
        renderRevenueTrendCard(),
        // Revenue Mix is temporarily disabled.
        // renderRevenueMixCard(),
        renderVolumeCard(),
    ];
    return html `
        <section class="revenue-summary-charts" data-bind="visible: requestState() !== 'loading' && hasChartData()">
            ${chartCards}
        </section>
        <section class="revenue-summary-charts-empty" data-bind="visible: requestState() !== 'loading' && hasLoadedData() && !hasChartData()">
            ${LpEmpty({
        title: "No chart data",
        description: "Charts will appear when the selected filters return revenue data.",
        icon: "chart-line-up",
        size: "sm",
    })}
        </section>
    `;
}
